/**
 * `AddSiteStep` — ADR-0049 step 0 (Add site). Composes the existing
 * `DomainStep` (domain input + `POST /v1/sites`); on resume, surfaces
 * a Continue affordance that advances without re-creating the site.
 *
 * ADR-0050 §2.1 — the prior "OPTIONAL · ANALYZE SITE" card + Analyze
 * button + READY/COULDN'T pills are removed. Skill suggestions are now
 * detection-driven (the suggestion panel renders on Step 2 from the
 * `recommendSkillsForDetection` helper); the ADR-0040 LLM enrichment
 * pipeline is re-scoped to automatic, server-side, best-effort —
 * never a user-facing button — and stays dormant behind
 * `PROFILE_EXTRACTION_ENABLED`. The `useProfileExtraction` hook is
 * still mounted elsewhere by callers that consume the LLM-merge seam.
 */

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';


import { useSites } from '../../app/providers.js';
import type { View } from '../../app/view-router.js';
import { useC } from '../../lib/theme.js';
import { DomainStep } from './DomainStep.js';

const sansFont = "'Geist', sans-serif";

export interface AddSiteStepProps {
  /** Called after either (a) the DomainStep finishes creating the site
   *  OR (b) the merchant clicks "Continue" once they have an active
   *  site already (e.g. resume + override). */
  onNext: () => void;
  /** Forwarded to `DomainStep` for the plan-limit notice's "View
   *  plans" CTA (ADR-0143 §8). Optional — see `DomainStepProps`. */
  setView?: (v: View) => void;
}

export function AddSiteStep({ onNext, setView }: AddSiteStepProps): JSX.Element {
  const C = useC();
  const { activeSite } = useSites();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <DomainStep onCreated={onNext} {...(setView !== undefined ? { setView } : {})} />

      {/* When the merchant lands here with an active site already
          (resume / re-edit) the DomainStep above does NOT auto-advance,
          so surface an explicit Continue affordance. The Analyze
          affordance is removed per ADR-0050 §2.1 — detection drives
          the suggestion panel on Step 2. */}
      {activeSite !== null ? (
        <div className="flex mt-6">
          <button
            type="button"
            onClick={onNext}
            className="flex items-center justify-center gap-2 px-6 py-2.5"
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 14,
              borderRadius: 2,
            }}
          >
            Continue <ArrowRight size={15} />
          </button>
        </div>
      ) : null}
    </motion.div>
  );
}
